<template>
    <div class="progressbar">
        <div class="progress-points">
            <svg v-for="(_, i) in groups" 
                :key="i" width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12.5" cy="12.5" r="11.5" fill="white" :stroke="getColor(i)" stroke-width="2"/>
                <circle cx="12.5" cy="12.5" r="5.5" :fill="getColor(i)"/>
            </svg>
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12.5" cy="12.5" r="11.5" fill="white" :stroke="getSummaryPointColor()" stroke-width="2"/>
                <circle cx="12.5" cy="12.5" r="5.5" :fill="getSummaryPointColor()"/>
            </svg>
        </div>
        <div class="progress-lines">
            <div 
                v-for="(_, i) in groups" 
                :key="i"
                class="gray-progress"
            >
                <div class="green-progress" :style="{width: getGreenLineWidth(i) + '%'}"></div>
            </div>
        </div>
        <div class="point-names">
            <div
                v-for="(group, i) in groups" 
                :key="i"
         
            >
                {{ group.name }}
            </div>
            <div class="">
                Result
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Group } from "@/entities/questionaire/Group";
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Prop, Vue, Component } from "vue-property-decorator";

@Component({
    name: 'ProgressBar',
})
export default class ProgressBar extends Vue {
    @Prop()
    groups!: Group[] | null;

    @Prop()
    completeData!: boolean;

    @Prop()
    answers!: Answer[];

    getSummaryPointColor() {
    if (!this.groups || !this.answers) {
        return '#CCD0DC';
    }
    
    // Calculate total questions across all groups
    const totalQuestions = this.groups.reduce((sum, group) => 
        sum + group.properties.filter(x => !x.isHidden).length, 
        0
    );
    
    // Only highlight Result point if ALL questions are answered AND completeData is true
    if (this.answers.length >= totalQuestions ) {
        return '#667bf2';
    }
    
    return '#CCD0DC';
}

//&& this.completeData
    getColor(index: number) {
        if (this.groups) {
            const newArr: number[] = [];
            const groupsQuestionCountArray = this.groups.map(group => group.properties.filter(x => !x.isHidden).length); 
            let counter = 0;

            for (let i = 0; i < groupsQuestionCountArray.length; i++) {
                if (newArr.length === 0) {
                    newArr.push(groupsQuestionCountArray[i]);
                    counter = groupsQuestionCountArray[i];
                } else {
                    counter += groupsQuestionCountArray[i];
                    newArr.push(counter);
                }
            }
            
            if (index === 0 || this.answers!.length >= newArr[index - 1]) {
                return '#667bf2'; // ProxyMed primary color
            } else {
                return '#CCD0DC';
            }
        } else {
            return '#CCD0DC'; 
        }
    }

    getGreenLineWidth(index: number) {
        if (this.groups) {
            const newArr: number[] = [];
            const groupsQuestionCountArray = this.groups.map(group => group.properties.filter(x => !x.isHidden).length);
            let counter = 0;

            for (let i = 0; i < groupsQuestionCountArray.length; i++) {
                if (newArr.length === 0) {
                    newArr.push(groupsQuestionCountArray[i]);
                    counter = groupsQuestionCountArray[i];
                } else {
                    counter += groupsQuestionCountArray[i];
                    newArr.push(counter);
                }
            }
            
            if (index === 0) {
                const persent = (this.answers.length * 100) / newArr[0]
                return persent;
            } else {
                if (groupsQuestionCountArray[index] === 0) {
                    return 100;
                }
                const persent = ((this.answers.length - newArr[index - 1]) * 100) / (groupsQuestionCountArray[index])
                return persent > 0 ? persent : 0;
            }
        } else {
            return 0; 
        }
    }
}
</script>
  
<style lang="scss" scoped>
.progressbar {
    margin-bottom: 20px;
    padding: 0 10px;
    max-width: 100%;
    overflow-x: auto;
}

.progress-points {
    display: flex;
    justify-content: space-between;
    min-width: fit-content;
    
    svg {
        flex-shrink: 0;
        transition: all 0.3s ease;
        
        &:hover {
            transform: scale(1.1);
        }
    }
}

.progress-lines {
    display: flex;
    column-count: 1;
    position: relative;
    bottom: 13px;
    min-width: fit-content;
}

.gray-progress {
    height: 3px;
    max-width: 100%;
    width: 100%;
    background: rgba(226, 232, 240, 0.5);
    margin: 0 13px;
    border-radius: 2px;
    overflow: hidden;
}

.green-progress {
    height: 3px;
    max-width: 100%;
    width: 50%;
    background: linear-gradient(135deg, #2f45a5, #667bf2);
    border-radius: 2px;
    transition: width 0.4s ease;
    box-shadow: 0 0 8px rgba(102, 123, 242, 0.3);
}

.gray-progress:first-child {
    margin-left: 25px;  
}

.gray-progress:last-child {
    margin-right: 25px;
}

.point-names {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    min-width: fit-content;
}

.point-name {
   
    text-align: center;
    word-wrap: break-word;
    line-height: 1.4;
    transition: color 0.3s ease;
    
    &:hover {
        color: #2f45a5;
    }
}

/* Responsive Design */
@media (max-width: 1024px) {
    .progressbar {
        padding: 0 5px;
    }
    
    .point-names {
        font-size: 12px;
    }
    
    .point-name {
        max-width: 80px;
    }
}

@media (max-width: 768px) {
    .progressbar {
        margin-top: 15px;
        padding: 0 8px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        
        &::-webkit-scrollbar {
            height: 6px;
        }
        
        &::-webkit-scrollbar-track {
            background: rgba(226, 232, 240, 0.3);
            border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb {
            background: rgba(102, 123, 242, 0.3);
            border-radius: 3px;
            
            &:hover {
                background: rgba(102, 123, 242, 0.5);
            }
        }
    }
    
    .progress-points svg {
        width: 20px;
        height: 20px;
    }
    
    .gray-progress {
        height: 2px;
        margin: 0 10px;
    }
    
    .green-progress {
        height: 2px;
    }
    
    .gray-progress:first-child {
        margin-left: 20px;
    }
    
    .gray-progress:last-child {
        margin-right: 20px;
    }
    
    .point-names {
        font-size: 11px;
    }
    
    .point-name {
        max-width: 70px;
        font-size: 10px;
    }
}

@media (max-width: 480px) {
    .progressbar {
        margin-top: 10px;
        padding: 0 6px;
    }
    
    .progress-points svg {
        width: 18px;
        height: 18px;
    }
    
    .gray-progress {
        margin: 0 8px;
    }
    
    .gray-progress:first-child {
        margin-left: 18px;
    }
    
    .gray-progress:last-child {
        margin-right: 18px;
    }
    
    .point-names {
        font-size: 10px;
    }
    
    .point-name {
        max-width: 60px;
        font-size: 9px;
    }
}
</style>